/**
 * DebugPanel — Collapsible raw Power BI request/response viewer.
 * Admin-only; shows JSON debug data with copy + refresh controls.
 */

import { useState } from "react";

interface DebugPanelProps {
  debugData: unknown;
  debugLoading: boolean;
  debugError: string | null;
  onDebug: () => void;
  onClose: () => void;
}

export function DebugPanel({
  debugData,
  debugLoading,
  debugError,
  onDebug,
  onClose,
}: DebugPanelProps) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
            />
          </svg>
          <h2 className="text-sm font-semibold text-gray-700">
            Power BI Debug
          </h2>
          <span className="text-xs text-gray-400 font-normal">
            Raw Request & Response
          </span>
        </div>
        <div className="flex items-center gap-2">
          <>
            {debugData && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    JSON.stringify(debugData, null, 2),
                  );
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                  copied
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
                }`}
              >
                {copied ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5 inline -mt-0.5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5 inline -mt-0.5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy JSON
                  </>
                )}
              </button>
            )}
            <button
              onClick={onDebug}
              disabled={debugLoading}
              className="text-xs px-2.5 py-1 rounded-md font-medium transition-colors bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
            >
              <svg
                className="w-3.5 h-3.5 inline -mt-0.5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
            <button
              onClick={onClose}
              className="text-xs px-2.5 py-1 rounded-md font-medium transition-colors bg-white text-gray-500 border border-gray-200 hover:bg-gray-100"
            >
              ✕
            </button>
          </>
        </div>
      </div>
      <div
        className="p-4 overflow-auto bg-[#1e1e2e]"
        style={{ maxHeight: 500 }}
      >
        {debugLoading ? (
          <div className="flex items-center gap-2 text-gray-400 font-mono text-sm py-4">
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Fetching debug data...
          </div>
        ) : debugError ? (
          <div className="flex items-center gap-2 text-red-400 font-mono text-sm">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {debugError}
          </div>
        ) : debugData ? (
          <pre className="text-[#cdd6f4] font-mono text-xs leading-relaxed whitespace-pre-wrap selection:bg-blue-500/30">
            {JSON.stringify(debugData, null, 2)}
          </pre>
        ) : (
          <p className="text-gray-500 font-mono text-sm">
            Click Debug button to load...
          </p>
        )}
      </div>
    </div>
  );
}
